// Wind forecast from Open-Meteo, fetched through the Worker (which adds caching and, later, a key).

import type { LngLat } from '../lib/geo';
import type { WindForecast, WindSample, WindSource } from './types';

const LEVELS = [900, 850, 800, 700] as const;

interface OpenMeteoHourly {
  time: string[];
  [k: string]: (number | null)[] | string[];
}

/** Turn an Open-Meteo `hourly` block into per-hour wind levels. Exported for tests. */
export function parseOpenMeteo(hourly: OpenMeteoHourly, groundAlt: number | null): WindForecast['hours'] {
  const num = (k: string, i: number) => {
    const v = (hourly[k] as (number | null)[] | undefined)?.[i];
    return typeof v === 'number' ? v : null;
  };
  return hourly.time.map((time, i) => {
    const levels: WindSample[] = [];
    const s10 = num('wind_speed_10m', i);
    const d10 = num('wind_direction_10m', i);
    if (s10 !== null && d10 !== null)
      levels.push({ time, speed: s10, from: d10, level: '10 m', altitude: groundAlt === null ? undefined : groundAlt + 10 });
    for (const p of LEVELS) {
      const s = num(`wind_speed_${p}hPa`, i);
      const d = num(`wind_direction_${p}hPa`, i);
      const h = num(`geopotential_height_${p}hPa`, i);
      if (s === null || d === null) continue;
      // Skip levels below the ground.
      if (h !== null && groundAlt !== null && h < groundAlt) continue;
      levels.push({ time, speed: s, from: d, level: `${p} hPa`, altitude: h ?? undefined });
    }
    return { time, levels };
  });
}

export class OpenMeteoWindSource implements WindSource {
  readonly id = 'open-meteo';
  readonly attribution = 'Wind: <a href="https://open-meteo.com/">Open-Meteo</a> (CC BY 4.0)';
  constructor(private apiBase: string) {}

  async forecast(at: LngLat): Promise<WindForecast> {
    const u = `${this.apiBase}/wind?lat=${at[1].toFixed(3)}&lon=${at[0].toFixed(3)}`;
    const res = await fetch(u);
    if (!res.ok) throw new Error(`Wind forecast unavailable (${res.status})`);
    const j = (await res.json()) as { hourly: OpenMeteoHourly; elevation?: number };
    return { hours: parseOpenMeteo(j.hourly, j.elevation ?? null), attribution: this.attribution };
  }
}

/** Pick the level whose altitude is closest to `alt` (falls back to the first). */
export function nearestLevel(levels: WindSample[], alt: number): WindSample | undefined {
  let best: WindSample | undefined;
  let bd = Infinity;
  for (const l of levels) {
    const d = l.altitude === undefined ? 1e9 : Math.abs(l.altitude - alt);
    if (d < bd) {
      bd = d;
      best = l;
    }
  }
  return best ?? levels[0];
}
